/**
 * The dossier view: facet sections over one artifact version, each row opening the
 * shared evidence panel with ±30 messages of interleaved container context (proposal,
 * "Impact" — the same context endpoint every other surface uses, just a wider window).
 *
 * Two distinctions the proposal calls load-bearing are rendered here rather than left
 * to be inferred from a blank space:
 *
 * - **`gap` vs `not_asked`** — "searched, absent" is not "never examined". Distinct
 *   labels, distinct test ids, distinct styling: a reader cannot mistake one for the
 *   other, and neither one is ever a blank facet.
 * - **Coverage, per producer, never collapsed** — a quiet actor and an unexamined one
 *   can produce an identically thin dossier; only the coverage section tells them
 *   apart, so every producer's statement is always visible, never averaged away.
 *
 * A derived row (volume, first/last activity) shows the operator and citation count
 * up front and expands into the full support set on demand — the same "count the
 * quotes yourself" affordance `ArtifactTable`'s derived cells already give a table.
 */

import { useEffect, useState } from "react";
import { EvidencePanel } from "../components/EvidencePanel";
import {
  api,
  isDossierLayout,
  type ArtifactVersion,
  type DossierFacet,
  type Unit,
} from "../lib/api";
import { useEvidence } from "../state/evidence";

interface Props {
  datasetId: string;
  artifactId: string;
  versionId: string;
}

// The proposal's own number ("±30 messages of container context"), wider than the
// panel's ordinary default of 8 — a dossier row is read in isolation far more often
// than a chat turn is, so it needs more of the conversation around it to make sense.
const DOSSIER_CONTEXT_WINDOW = 30;

export function Dossier({ datasetId, artifactId, versionId }: Props) {
  const [version, setVersion] = useState<ArtifactVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .version(datasetId, artifactId, versionId)
      .then((fetched) => {
        if (!cancelled) setVersion(fetched);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId, artifactId, versionId]);

  if (error) {
    return (
      <main className="page">
        <p data-testid="dossier-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      </main>
    );
  }
  if (!version) return <main className="page">loading…</main>;

  if (!isDossierLayout(version.layout)) {
    return (
      <main className="page">
        <p data-testid="dossier-wrong-kind" style={{ color: "var(--danger)" }}>
          This artifact version is not a dossier.
        </p>
      </main>
    );
  }
  const layout = version.layout;

  return (
    <div className="workspace two-pane">
      <section className="pane left">
        <main className="page dossier" data-testid="dossier-view">
          <header>
            <h1>{version.title}</h1>
            <p className="eyebrow" data-testid="dossier-status">
              {layout.identity} · v{version.version_no} · {version.status}
              {version.stale ? " · stale" : ""}
            </p>
            <p>
              <a
                href={api.exportUrl(datasetId, artifactId, versionId)}
                data-testid="export-link"
                download
              >
                Export bundle (.zip)
              </a>{" "}
              <span className="mono" style={{ color: "var(--ink-faint)" }}>
                includes an offline verifier that re-checks every quote and every
                coverage statement
              </span>
            </p>
          </header>

          {layout.facets.map((facet) => (
            <FacetSection key={facet.facet} facet={facet} units={version.units} />
          ))}

          <section className="coverage-section" data-testid="dossier-coverage">
            <p className="eyebrow">Coverage — how much was read, per producer</p>
            <p style={{ color: "var(--ink-faint)", maxWidth: "60ch" }}>
              A thin section above can mean the actor was quiet, or that the producer
              barely looked. Only the statements below tell those apart — never
              averaged into one number.
            </p>
            {layout.coverage.map((ref) => {
              const unit = version.units[ref.u];
              if (!unit) return null;
              return (
                <p key={ref.u} data-testid="coverage-row" className="coverage-row">
                  {unit.text}
                </p>
              );
            })}
            {layout.coverage.length === 0 && (
              <p data-testid="coverage-missing" style={{ color: "var(--danger)" }}>
                No coverage statement travelled with this version — it should never
                have been possible to finalize.
              </p>
            )}
          </section>
        </main>
      </section>
      <EvidencePanel datasetId={datasetId} contextWindow={DOSSIER_CONTEXT_WINDOW} />
    </div>
  );
}

function FacetSection({
  facet,
  units,
}: {
  facet: DossierFacet;
  units: Record<string, Unit>;
}) {
  return (
    <section className="facet" data-testid="dossier-facet" data-facet={facet.facet}>
      <h2>{facet.label}</h2>
      {facet.rows.map((ref) => {
        const unit = units[ref.u];
        if (!unit) return null;
        return <FacetRow key={ref.u} unit={unit} />;
      })}
    </section>
  );
}

function FacetRow({ unit }: { unit: Unit }) {
  const { select, selected } = useEvidence();
  const [expanded, setExpanded] = useState(false);

  // The two kinds of empty (evidence-ui spec, "Absence is rendered with its cause"):
  // distinct test ids and distinct wording, never a shared "empty" state.
  if (unit.kind === "gap") {
    return (
      <p className="dossier-row gap" data-testid="facet-gap">
        searched, absent — this producer examined the identity's evidence for this
        facet and found nothing.
      </p>
    );
  }
  if (unit.kind === "not_asked") {
    return (
      <p className="dossier-row not-asked" data-testid="facet-not-asked">
        not yet examined — {unit.text}
      </p>
    );
  }

  const isDerived = unit.kind === "derived";
  // identity-dossiers design D3: a contacts row's counterpart and edge kind live in
  // `value`, not `text` (the recomputed re-execution overwrites `text` with the bare
  // count, the same convention every derived unit in this dossier follows) — this is
  // the one place that composes them back into a readable row.
  const isContactRow = isDerived && unit.value?.counterpart_display_value !== undefined;
  const display = isContactRow
    ? `"${unit.value?.counterpart_display_value}" (${unit.value?.edge_kind}, ${unit.text ?? unit.value?.v ?? "0"})`
    : (unit.text ?? unit.value?.v ?? "");
  const operator = unit.computation ? String((unit.computation as { op?: string }).op ?? "derived") : "derived";

  // identity-dossiers design D4: review state rides either the unit's own `value`
  // (a contacts row, folded from `link_verdicts` at generation time) or the first
  // link-derived citation (a surface-form row, whose evidence is one member's own
  // merge — never a mix of members, so the first is the row's own state). Read fresh
  // by the server on every request, never cached in this component.
  const reviewState =
    unit.value?.review_state ?? unit.citations.find((c) => c.link_derived)?.review_state ?? null;
  const reviewLabel =
    reviewState === "proposed"
      ? "unreviewed"
      : reviewState === "confirmed"
        ? "confirmed"
        : reviewState === "rejected"
          ? "rejected"
          : null;

  return (
    <div className={`dossier-row ${unit.kind}`} data-testid="facet-row" data-kind={unit.kind}>
      {reviewLabel && (
        <span data-testid="facet-row-review-state" className="eyebrow">
          {reviewLabel === "unreviewed" ? "⚠ unreviewed" : reviewLabel}
        </span>
      )}
      <span data-testid="facet-row-value">{display}</span>

      {isDerived && (
        <button
          className="derived-mark"
          data-testid="facet-row-derived"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          title="Computed from the citations below, not taken from the model"
        >
          {operator} of {unit.citations.length}
        </button>
      )}

      {(!isDerived || expanded) && (
        <span className="cites" data-testid="facet-row-cites">
          {unit.citations.map((citation, index) => {
            const isSelected =
              selected?.blockId === citation.block_id &&
              selected.spans[0]?.start_cp === citation.start_cp;
            return (
              <button
                key={citation.span_id}
                className="cite"
                data-testid="facet-cite"
                aria-current={isSelected}
                // Support score and method are labels, never a filter (spec,
                // "Confidence is soft, labelled, and never a gate") — shown in the
                // title rather than hidden behind a threshold that would remove the
                // row entirely.
                title={`${citation.quote}${
                  citation.support_score !== null
                    ? ` · support ${citation.support_score.toFixed(2)}`
                    : ""
                }${citation.support_method ? ` (${citation.support_method})` : ""}`}
                onClick={() =>
                  select({
                    blockId: citation.block_id,
                    spans: [
                      {
                        start_cp: citation.start_cp,
                        end_cp: citation.end_cp,
                        quote: citation.quote,
                      },
                    ],
                    label: `${index + 1}`,
                    origin: "dossier",
                  })
                }
              >
                {index + 1}
              </button>
            );
          })}
        </span>
      )}
    </div>
  );
}
